import { ArrowLeftIcon } from "@phosphor-icons/react/dist/csr/ArrowLeft";
import { Formik, Form } from "formik";
import { useNavigate } from "react-router-dom";
import * as Yup from "yup";
import { Button } from "@volca/ui/components/ui/button";
import { Card } from "@volca/ui/components/ui/card";
import { FormInput } from "@volca/ui/components/ui/form-input";
import { Stack } from "@volca/ui/components/ui/stack";
import { Text } from "@volca/ui/components/ui/text";
import { toast } from "@volca/ui/lib/toast";
import {
  isPasswordResetThrottledError,
  showThrottledResetToast,
  useAuth,
} from "@/core/auth";

const forgotPasswordSchema = Yup.object().shape({
  email: Yup.string()
    .email("Invalid email address")
    .required("Email is required"),
});

export default function ForgotPassword() {
  const { forgotPassword, openResetCodeEntry } = useAuth();
  const navigate = useNavigate();

  return (
    <Stack
      gap={4}
      direction="row"
      align="center"
      justify="center"
      className="min-h-full w-full overflow-hidden p-6"
    >
      <Card className="w-full max-w-md gap-0 p-6 pt-8">
        <Formik
          initialValues={{ email: "" }}
          validationSchema={forgotPasswordSchema}
          validateOnMount
          onSubmit={async (values, { setSubmitting }) => {
            try {
              await forgotPassword(values);
            } catch (error) {
              if (isPasswordResetThrottledError(error)) {
                showThrottledResetToast(error.message, () =>
                  openResetCodeEntry(values.email),
                );
                return;
              }
              toast.error(
                error instanceof Error
                  ? error.message
                  : "Couldn't send reset instructions. Please try again.",
              );
            } finally {
              setSubmitting(false);
            }
          }}
        >
          {({ getFieldProps, isSubmitting, isValid }) => (
            <Form>
              <Stack className="w-full" align="start" gap={6}>
                <Text variant="title" render={<h1 />} className="mt-4">
                  Forgot your password?
                </Text>

                <Text tone="muted">
                  Enter your email address and we will send you reset
                  instructions.
                </Text>

                <FormInput
                  id="email"
                  type="email"
                  label="Email"
                  placeholder="Enter your email"
                  {...getFieldProps("email")}
                  fullWidth
                />

                <Stack className="w-full" align="start" gap={2}>
                  <Button
                    type="submit"
                    size="lg"
                    disabled={!isValid || isSubmitting}
                    className="w-full"
                    loading={isSubmitting}
                  >
                    Reset password
                  </Button>

                  <Button
                    type="button"
                    size="lg"
                    variant="ghost"
                    onClick={() => navigate("/login")}
                    className="w-full"
                  >
                    <ArrowLeftIcon data-icon="inline-start" />
                    Back to sign in
                  </Button>
                </Stack>
              </Stack>
            </Form>
          )}
        </Formik>
      </Card>
    </Stack>
  );
}
